
import { Enums } from '@bode-canada/core';
import deepmerge from 'deepmerge';
import {
  StyleColorSchemeType, StyleFontWeightType, StyleSizeType,
} from '@/ui-kit/styles/enums';
import { TooltipMessage } from '@/ui-kit/components/tooltip/enum';
import { ButtonFillType } from '@/ui-kit/components/button/enums';
import { GutterProperty } from '@/ui-kit/components/gutter/enums';
import { TypographyType, TypographyProperty } from '@/ui-kit/components/typography/enums';
import SpinnerModal from '@/ui-kit/components/modal/spinner';
import ModalService from '@/ui-kit/components/modal/ModalService';
import SoldDataCardImage from '@/assets/images/sold-data-card.jpg';
import MarketDataCardImage from '@/assets/images/market-data-card.jpg';
import ListHomeCardImage from '@/assets/images/list-home-card.jpg';
import ActiveListingCardImage from '@/assets/images/active-listing-card.jpg';
import { QueryParams } from '@/constants/QueryParams';
import ListingSection from '@/components/page-specific/listing/section';
import ListingLicence from '@/components/page-specific/listing/licence';
import ListingTransactionTip from '@/components/page-specific/listing/transaction-tip';
import ListingTransactionHistory from '@/components/page-specific/listing/transaction-history';
import ListingSummary from '@/components/page-specific/listing/summary';
import ListingGallery from '@/components/page-specific/listing/gallery';
import ListingBodeLabel from '@/components/page-specific/listing/bode-label';
import ListingPropertyOverview from '@/components/page-specific/listing/property-overview';
import ListingNavigationCard from '@/components/page-specific/listing/navigation-card';
import ListingMap from '@/components/page-specific/listing/map';
import ListingOverviewHelper from '@/components/page-specific/listing/property-overview/helpers';
import ListingAccordion from '@/components/page-specific/listing/accordion';
import ListingSoldTrendsChart from '@/components/page-specific/listing/sold-trends-chart';
import ListingAccordionHelper from '@/components/page-specific/listing/accordion/helpers';
import { ListingGallerySlotName } from '@/components/page-specific/listing/gallery/enums';
import {
  ListingNavigationCardProperty,
  ListingNavigationCardVariant,
} from '@/components/page-specific/listing/navigation-card/enums';
import { ContainerType, SectionFillType } from '@/components/page-specific/listing/section/enums';
import routes from '@/constants/routes';
import NavigationLinksMixin from '@/components/page-specific/listing/mixins/navigation-links';
import SoldPropertyHeadMixin from '@/components/features/sold-property/mixins/SoldPropertyHeadMixin';
import HistoryTabsMixin from '@/components/features/sold-property/mixins/HistoryTabsMixin';
import ActiveListingMixin from '@/components/features/sold-property/mixins/ActiveListingMixin';
import SoldPropertyActions from '@/components/features/sold-property/components/SoldPropertyActions';
import { icons } from '@/components/maps/constants';
import {
  ListingTransactionHistoryProperty,
  ListingTransactionHistoryEventType,
} from '@/components/page-specific/listing/transaction-history/enums';
import { PropertyOverviewTitle } from '@/components/page-specific/listing/property-overview/enums';
import NewListingSoldApiService from '@/components/features/sold-property/api/NewListingSoldApiService';
import LoadListingService from '@/components/page-specific/listing/services/LoadListingService';
import Typography from '@/ui-kit/components/typography/index';
import PropertyOwnershipApiService from '@/services/api/serverless/property-ownership/PropertyOwnershipApiService';
import ToastService from '@/components/toast/toast-service';
import {
  ADD_PROPERTY_OWNERSHIP,
  getAction,
  REFRESH,
} from '@/store/user/actions';
import SellingMotivationModal from '@/components/features/sold-property/components/SellingMotivationModal';
import {
  SELLING_MOTIVATION_EVENTS_MAPPING,
} from '@/components/features/sold-property/constants/SellingMotivationConstants';
import userStore from '@/utils/user/cache';
import useCanonicalHead from '@/mixins/useCanonicalHead';

const DEFAULT_ACTIVE_LISTING_INDEX = 0;
const KEY_DETAILS_COLUMNS_COUNT = 3;
const CLAIM_OWNERSHIP_POSITIVE_MESSAGE = 'The property ownership was successfully claimed.';

export default {
  name: 'PropertyPage',
  components: {
    Typography,
    ListingSection,
    ListingLicence,
    ListingTransactionTip,
    ListingTransactionHistory,
    ListingSummary,
    ListingGallery,
    ListingBodeLabel,
    ListingPropertyOverview,
    ListingNavigationCard,
    ListingMap,
    ListingAccordion,
    ListingSoldTrendsChart,
    SoldPropertyActions,
  },
  mixins: [
    NavigationLinksMixin,
    HistoryTabsMixin,
    ActiveListingMixin,
    SoldPropertyHeadMixin,
    useCanonicalHead(),
  ],
  asyncData({ payload }) {
    return payload ? { ...payload, isLoaded: true } : {};
  },
  data() {
    return {
      SoldDataCardImage,
      ListHomeCardImage,
      ActiveListingCardImage,
      MarketDataCardImage,
      activeTransactionIndex: DEFAULT_ACTIVE_LISTING_INDEX,
      listingsDto: new Map(),
      isLoaded: false,
      isOwnershipClaiming: false,
      icons,
      ListingGallerySlotName,
      StyleColorSchemeType,
      StyleSizeType,
      ContainerType,
      SectionFillType,
      TooltipMessage,
      ButtonFillType,
      ListingNavigationCardVariant,
      ListingNavigationCardProperty,
      ListingTransactionHistoryProperty,
      ListingTransactionHistoryEventType,
      StyleFontWeightType,
      KEY_DETAILS_COLUMNS_COUNT,
      TypographyProperty,
      TypographyType,
      GutterProperty,
    };
  },
  head() {
    return deepmerge.all([this.headOG, this.headCanonical]);
  },
  computed: {
    activeTransaction() {
      return this.transactions[this.activeTransactionIndex] || null;
    },
    keyDetailsCharacteristics() {
      return ListingOverviewHelper.getKeyDetails(
        this.activeTransaction,
        this.listing.isBode,
        this.latestSoldTransaction?.price,
      );
    },
    propertyOverviewCharacteristics() {
      return ListingOverviewHelper.getListingPropertyOverviewCharacteristics(
        this.listing,
        [
          PropertyOverviewTitle.MLSNumber,
          PropertyOverviewTitle.TimeOnBode,
          PropertyOverviewTitle.DaysOnMarket,
          PropertyOverviewTitle.DaysOnPlatform,
        ],
      );
    },
    accordionsConfiguration() {
      return ListingAccordionHelper.getShortListingAccordions(this.listing);
    },
    hasImages() {
      return !!this.listing.images?.length;
    },
    shareUrl() {
      return window.location.href;
    },
    propertyId() {
      return this.listing.propertyId;
    },
  },
  async mounted() {
    // NOTE: fetch fresh data on client-side
    await this.fetchPropertyListings();
  },
  methods: {
    async fetchPropertyListings() {
      this.transactionsDto = await this.fetchSoldPropertyTransactions();
      if (!this.transactionsDto.length) {
        console.error(`No transactions for property ${this.soldPropertyAddressId}`);
        await this.$router.push(routes.notFound);
        return;
      }
      const listingId = Number(this.$route.query[QueryParams.ListingId]);
      await this.setActiveListing(listingId);
      this.isLoaded = true;
    },
    async fetchSoldPropertyTransactions() {
      try {
        return await NewListingSoldApiService.getSoldTransactionsByAddressId(this.soldPropertyAddressId);
      } catch {
        return [];
      }
    },
    setActiveTransaction(listingId) {
      this.activeTransactionIndex = this.getTransactionIndexByListingId(listingId);
      this.replaceRoute(this.activeTransaction.listingId);
    },
    async setActiveListing(listingId) {
      this.setActiveTransaction(listingId);
      const listingDto = await this.getListingDto(this.activeTransaction);
      if (!listingDto) {
        console.error(`No listing for id ${this.activeTransaction.listingId}`);
        await this.$router.push(routes.notFound);
        return;
      }
      this.listingDto = listingDto;
    },
    getTransactionIndexByListingId(id) {
      const indexByListing = this.transactions.findIndex(({ listingId }) => listingId === id);
      if (indexByListing >= 0) {
        return indexByListing;
      }
      const indexByStatus = this.transactions.findIndex(({ status }) => status !== Enums.ListingStatusEnum.HIDDEN);
      return indexByStatus >= 0 ? indexByStatus : DEFAULT_ACTIVE_LISTING_INDEX;
    },
    replaceRoute(listingId) {
      return this.$router.replace({
        path: this.$route.path,
        query: {
          listingId,
        },
        replace: true,
      }).catch(console.error);
    },
    async getListingDto(transaction) {
      const { listingId } = transaction;

      if (!this.listingsDto.has(listingId)) {
        await this.loadListing(transaction);
      }

      return this.listingsDto.get(listingId);
    },
    async loadListing(transaction) {
      ModalService.open({ component: SpinnerModal });

      const { listingId } = transaction;
      try {
        const listingDto = await LoadListingService.loadListing(listingId);

        this.listingsDto.set(listingId, {
          ...listingDto,
          listing: {
            ...listingDto.listing,
            ...transaction,
            shareUrl: this.shareUrl,
            soldDate: transaction.soldDate,
          },
        });
      } catch {
        // Ignore error
      } finally {
        ModalService.close();
      }
    },
    async handleCreateDashboardClick() {
      const sellingMotivationType = await ModalService.open({
        component: SellingMotivationModal,
      });

      if (!sellingMotivationType) {
        return;
      }

      this.$track(SELLING_MOTIVATION_EVENTS_MAPPING.get(sellingMotivationType));

      try {
        this.isOwnershipClaiming = true;
        const claimOwnershipDto = {
          sellingMotivationType,
        };
        const ownership = await PropertyOwnershipApiService.claimOwnership(this.propertyId, claimOwnershipDto);
        this.setOwnership(ownership);
        userStore.set({ propertyOwnerships: [ownership] });
        await this.$store.dispatch(getAction(ADD_PROPERTY_OWNERSHIP), ownership);
        ToastService.openPositive(CLAIM_OWNERSHIP_POSITIVE_MESSAGE);
        this.$router.push(routes.homeownerDashboard);
      } catch (error) {
        ToastService.openNegative(error.message);
        await this.$store.dispatch(getAction(REFRESH));
      } finally {
        this.isOwnershipClaiming = false;
      }
    },
  },
};
